import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useParams } from '@/providers/RouterShim';
import type { RequestStateQuery } from '../../@types/RequestStateQuery';
import { getJSON } from '../../fetch';
import useAuth from '../../hooks/useAuth';
import type { VehicleListItem } from '../vehicles/useVehicles';

interface HubspotKnowledgeItem {
  category: string;
  description: string;
  domain: string;
  id: number;
  language: string;
  score: number;
  title: string;
  type: 'KNOWLEDGE_ARTICLE' | 'BLOG_POST';
  url: string;
}

export interface QuickSearchResponse {
  vehicle_hits: VehicleListItem[];
  hubspot_hits: HubspotKnowledgeItem[];
  total_hit_count: number;
  vehicle_query: string;
  hubspot_query: string;
}

const useQuickSearch = (
  query: string,
  useAi: boolean,
): [QuickSearchResponse | undefined, RequestStateQuery] => {
  const { branchId } = useParams();
  const { getToken } = useAuth();
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [query, useAi]);

  const { data, isLoading, isSuccess, isError } = useQuery<QuickSearchResponse>({
    queryKey: ['quickSearch', branchId, debouncedQuery, useAi],
    queryFn: async () => {
      return await getJSON<QuickSearchResponse>(
        `${process.env.NEXT_PUBLIC_PROCESS_FLOW_URI}/explore/${branchId}/federated?query=${debouncedQuery}&ai=${useAi}`,
        getToken()!,
      );
    },
    enabled: !!debouncedQuery && debouncedQuery.length >= 2,
  });

  return [data, { isLoading, isSuccess, isError }];
};

export default useQuickSearch;
